import { defineStore } from 'pinia'
import ApiService from '@/services/ApiService'
import { computed, ref, watch } from 'vue'

export const useGlobalStore = defineStore('global', () => {
    // In-memory cache for static reference data — cleared on page reload only
    const _cache = {
        countries: null,
        currencies: null,
        enums: {},
    }

    const settings = ref({
        site: {},
        languages: [[
            {
                "id": 38,
                "code": "en",
                "name": "English",
                "symbol": null
            },
            {
                "id": 40,
                "code": "es",
                "name": "Spanish",
                "symbol": null
            },
            {
                "id": 48,
                "code": "fr",
                "name": "French",
                "symbol": null
            }
        ]],
    })

    const availableLanguages = computed(() => settings.value.languages)
    const site = computed(() => settings.value.site)
    const roles = computed(() => site.value.roles)

    /**
     * Cache storage
     */
    const settingsInStorage = localStorage.getItem('globalSettings')
    if (settingsInStorage) {
        const storageSettings = JSON.parse(settingsInStorage)
        settings.value = {
            ...settings.value,
            ...storageSettings,
        }
    }
    watch(
        () => settings,
        (state) => {
            localStorage.setItem('globalSettings', JSON.stringify(state.value))
        },
        { deep: true }
    )

    async function init() {
        if (!settings.value.languages.length || settings.value.languages.length < 3) {
            await getAvailableLanguages()
        }

        await getSiteSettings(import.meta.env.VITE_SITE_UUID)
    }

    /**
     * Get available languages
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function getAvailableLanguages() {
        return ApiService.get('languages')
            .then(({ data }) => {
                settings.value.languages = data.data
                return data
            })
            .catch(({ data }) => {
                throw data.message
            })
    }

    /**
     * Get available countries
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function getAvailableCountries() {
        return ApiService.get('countries')
            .then(({ data }) => {
                return data
            })
            .catch(({ data }) => {
                throw data.message
            })
    }

    /**
     * Get available enum type (cached for session lifetime)
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function getEnum(_type) {
        if (_cache.enums[_type]) return _cache.enums[_type]
        return ApiService.get(`enum/${_type}`)
            .then(({ data }) => {
                _cache.enums[_type] = data
                return data
            })
            .catch(({ data }) => {
                throw data.message
            })
    }

    /**
     * Get available currencies (cached for session lifetime)
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function getCurrencies() {
        if (_cache.currencies) return _cache.currencies
        return ApiService.get(`currencies`)
            .then(({ data }) => {
                _cache.currencies = data
                return data
            })
            .catch(({ data }) => {
                throw data.message
            })
    }

    /**
     * Get available countries (cached for session lifetime)
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function getCountries() {
        if (_cache.countries) return _cache.countries
        return ApiService.get(`countries`)
            .then(({ data }) => {
                _cache.countries = data
                return data
            })
            .catch(({ data }) => {
                throw data.message
            })
    }

    const getSiteSettings = async (_uuid) => {
        return ApiService.get(`sites/${_uuid}/uuid`)
            .then(({ data }) => {
                settings.value = {
                    ...settings.value,
                    site: {
                        ...data
                    }
                }
                return data
            })
            .catch((e) => {
                throw e
            })
    }

    return {
        settings,
        availableLanguages,
        roles,
        init,
        getAvailableCountries,
        getAvailableLanguages,
        getCurrencies,
        getCountries,
        getEnum,
        getSiteSettings,
    }
})
